import { useState, useCallback } from 'react';
import { GameBoard } from '../GameBoard';
import { ConnectingScreen } from './ConnectingScreen';
import { useGameState, useConnection } from '../../hooks';
import { GameAction } from '../../types/game';

interface GameScreenProps {
  roomId: string;
  onBackToLobby: () => void;
}

export function GameScreen({ roomId, onBackToLobby }: GameScreenProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [combiningCards, setCombiningCards] = useState<string[] | null>(null);

  const gameStateHook = useGameState();
  const { gameState, playerId, isSpectator, spectatorId } = gameStateHook;

  const handleCombineAnimation = useCallback((cardIds: [string, string]) => {
    setCombiningCards(cardIds);
  }, []);

  const handleCombineAnimationEnd = useCallback(() => {
    setCombiningCards(null);
  }, []);

  const connection = useConnection({
    roomId,
    enabled: true,
    gameStateHook,
    onGameStart: () => setIsPlaying(true),
    onError: setConnectionError,
    onCombineAnimation: handleCombineAnimation,
    onCombineAnimationEnd: handleCombineAnimationEnd,
  });

  // Game actions
  const handleCombine = useCallback((cardId1: string, cardId2: string) => {
    if (!gameState || !playerId || isSpectator) return;
    if (gameState.currentTurn !== playerId) return;

    const action: GameAction = { type: 'combine', cardId1, cardId2 };
    const success = gameStateHook.executeAction(playerId, action);

    if (success) {
      connection.sendMove(action);
    }
  }, [gameState, playerId, isSpectator, gameStateHook, connection]);

  const handleDiscard = useCallback((cardIds: string[]) => {
    if (!gameState || !playerId || isSpectator) return;
    if (gameState.currentTurn !== playerId) return;

    const action: GameAction = { type: 'discard', cardIds };
    const success = gameStateHook.executeAction(playerId, action);

    if (success) {
      connection.sendMove(action);
    }
  }, [gameState, playerId, isSpectator, gameStateHook, connection]);

  const handleEndTurn = useCallback(() => {
    if (!gameState || !playerId || isSpectator) return;
    if (gameState.currentTurn !== playerId) return;

    const action: GameAction = { type: 'end_turn' };
    const success = gameStateHook.executeAction(playerId, action);

    if (success) {
      connection.sendMove(action);
    }
  }, [gameState, playerId, isSpectator, gameStateHook, connection]);

  const handleSurrender = useCallback(() => {
    if (!gameState || !playerId || isSpectator) return;
    if (gameState.winner !== null) return;

    const action: GameAction = { type: 'surrender' };
    const success = gameStateHook.executeAction(playerId, action);

    if (success) {
      connection.sendMove(action);
    }
  }, [gameState, playerId, isSpectator, gameStateHook, connection]);

  // Show connecting screen until game starts
  if (!isPlaying || !gameState) {
    return (
      <ConnectingScreen
        roomId={roomId}
        isConnecting={connection.isConnecting}
        isConnected={connection.isConnected}
        playerId={playerId}
        isSpectator={isSpectator}
        spectatorId={spectatorId}
        error={connectionError || connection.connectionError}
        onBack={onBackToLobby}
      />
    );
  }

  // Show game board
  return (
    <GameBoard
      gameState={gameState}
      playerId={playerId || 1}
      isSpectator={isSpectator}
      onCombine={isSpectator ? () => {} : handleCombine}
      onDiscard={isSpectator ? () => {} : handleDiscard}
      onEndTurn={isSpectator ? () => {} : handleEndTurn}
      onSurrender={isSpectator ? () => {} : handleSurrender}
      onBackToMenu={onBackToLobby}
      combiningCards={combiningCards}
    />
  );
}
